"use client";

import { Icon, VirtualTableView, ospThemes } from "@ospweb/sdk-ui";
import React, { useMemo } from "react";
import type { ColumnsType } from "antd/es/table";
import { Tooltip } from "antd";
import bigDecimal from "js-big-decimal";
import { cn } from "utils";
import { FormattedPrice } from "../../PriceDisplay/FormattedPrice";
import { PortfolioType } from "../types";
import { Transaction, TransactionType } from "./types";

import styles from "./TransactionTable.module.css";
import { I18nUtil } from "@utils/i18n";
import { Stock } from "@/src/features/api/openData";
import Translations from "@translate/components/Portfolio/Transactions/TransactionTable";
import { formatBigNumber } from "@/src/utils";
import { getCurrencySymbol } from "@utils/stock";
import { useIsPc } from "@/src/hooks";
import { usePortfolioType } from "@/src/hooks/usePortfolioType";

export interface TransactionTableProps {
  className?: string;
  data: Transaction[];
  onEdit: (transaction: Transaction) => void;
  onDelete: (transaction: Transaction) => void;
  enableInfiniteScroll?: boolean;
  onLoadMore?: () => void | Promise<void>;
  loadingMore?: boolean;
  hasMore?: boolean;
  loadMoreThreshold?: number;
  loadingMoreContent?: React.ReactNode;
  noMoreContent?: React.ReactNode;
  isLoading?: boolean;
  currency?: string;
}

const typeIconMap: Record<TransactionType, string> = {
  [TransactionType.Buy]: "import",
  [TransactionType.Sell]: "export",
};

const typeClassMap: Record<TransactionType, string> = {
  [TransactionType.Buy]: "text-content_accent1_primary",
  [TransactionType.Sell]: "text-content_accent2_primary",
};

const I18n = I18nUtil.createScoped(Translations);

export const TransactionTable: React.FC<TransactionTableProps> = ({
  className,
  data,
  onEdit,
  onDelete,
  enableInfiniteScroll,
  onLoadMore,
  loadingMore,
  hasMore,
  loadMoreThreshold,
  loadingMoreContent,
  noMoreContent,
  isLoading,
  currency = "USD",
}) => {
  const [isPc] = useIsPc();
  const { currentType } = usePortfolioType();
  const columns = useMemo<ColumnsType<Transaction>>(() => {
    const baseColumns = [
      {
        title: (
          <span className="body_medium_s text-content_secondary">
            {I18n.t("Type")}
          </span>
        ),
        dataIndex: "type",
        key: "type",
        width: isPc ? 140 : 76,
        render: (_: TransactionType, record: Transaction) => {
          const iconName = typeIconMap[record.type];
          const toneClass = typeClassMap[record.type];

          return (
            <div className="flex items-center gap-2">
              <Icon name={iconName} size={16} className={toneClass} />
              <span className={cn("body_medium_m", toneClass)}>
                {I18n.t(record.type === TransactionType.Buy ? "Buy" : "Sell")}
              </span>
            </div>
          );
        },
      },
      {
        title: (
          <span className="body_medium_s text-content_secondary whitespace-nowrap">
            {I18n.t("Quantity")}
          </span>
        ),
        dataIndex: "quantity",
        key: "quantity",
        width: isPc ? 120 : 67,
        render: (value: number) => (
          <span className="body_bold_m text-content_primary">
            {formatBigNumber(value, {
              needFormat: true,
              precision: 8,
              trimZeros: true,
            })}
          </span>
        ),
      },
      {
        title: (
          <span className="body_medium_s text-content_secondary">
            {I18n.t("Price")}
          </span>
        ),
        dataIndex: "pricePerCoin",
        key: "pricePerCoin",
        width: isPc ? 160 : 109,
        render: (value: number) => {
          return (
            <FormattedPrice
              price={value.toString()}
              type="token"
              currencySymbol={getCurrencySymbol(currency as Stock["currency"])}
              className={{
                container: "body_bold_m text-content_primary",
              }}
            />
          );
        },
      },
      {
        title: (
          <span className="body_medium_s text-content_secondary">
            {I18n.t("Date & Time")}
          </span>
        ),
        dataIndex: "dateTime",
        key: "dateTime",
        width: 165,
        render: (value: string) => (
          <span className="body_medium_m text-content_primary">{value}</span>
        ),
      },
      {
        title: (
          <span className="body_medium_s text-content_secondary">
            {I18n.t("Fees")}
          </span>
        ),
        dataIndex: "fees",
        key: "fees",
        width: 160,
        render: (value: number) => (
          <span className="body_bold_m text-content_primary">
            {getCurrencySymbol(currency as Stock["currency"])}
            {formatBigNumber(value, {
              needFormat: true,
              trimZeros: true,
              precision: 8,
            })}
          </span>
        ),
      },
      {
        title: (
          <span className="body_medium_s text-content_secondary">
            {I18n.t("Cost")}
          </span>
        ),
        dataIndex: "cost",
        key: "cost",
        width: 160,
        render: (value: number) => (
          <span className="body_bold_m text-content_primary">
            {value >= 0 ? "" : "-"}
            {getCurrencySymbol(currency as Stock["currency"])}
            {new bigDecimal(Math.abs(value)).getValue()}
          </span>
        ),
      },
      currentType !== PortfolioType.EXTERNAL
        ? {
            title: (
              <div className="flex items-center justify-center">
                <span className="body_medium_s text-content_secondary">
                  {I18n.t("Action")}
                </span>
              </div>
            ),
            key: "action",
            width: 120,
            render: (_value: unknown, record: Transaction) => (
              <div className="flex items-center justify-center">
                <Tooltip
                  placement="top"
                  arrow={false}
                  title={I18n.t("Edit")}
                  color={ospThemes.colors.background_solid_neutral_tertiary}
                  classNames={{
                    root: "rounded-1 border border-border_white_transparent10 py-[4px] px-[6px] bg-background_solid_neutral_tertiary body_xs text-content_primary",
                  }}
                >
                  <button
                    data-openreplay-label="Transaction Table Button"
                    id="btn-transaction-table"
                    type="button"
                    aria-label={I18n.t("Edit Transaction")}
                    className="flex size-5 items-center justify-center rounded-full text-content_brand_primary transition hover:text-content_brand_secondary"
                    onClick={() => onEdit(record)}
                  >
                    <Icon name="edit-alt" size={16} />
                  </button>
                </Tooltip>

                <Tooltip
                  placement="top"
                  arrow={false}
                  title={I18n.t("Delete")}
                  color={ospThemes.colors.background_solid_neutral_tertiary}
                  classNames={{
                    root: "rounded-1 border border-border_white_transparent10 py-[4px] px-[6px] bg-background_solid_neutral_tertiary body_xs text-content_primary",
                  }}
                >
                  <button
                    data-openreplay-label="Transaction Table Button"
                    id="btn-transaction-table-2"
                    type="button"
                    aria-label={I18n.t("Delete Transaction?")}
                    className="flex size-5 items-center justify-center rounded-full text-content_brand_primary transition hover:text-content_brand_secondary"
                    onClick={() => onDelete(record)}
                  >
                    <Icon name="trash" size={16} />
                  </button>
                </Tooltip>
              </div>
            ),
          }
        : null,
    ].filter(
      (column): column is NonNullable<typeof column> => column !== null,
    ) as ColumnsType<Transaction>;

    return baseColumns.map((column, columnIndex) => {
      const originalOnHeaderCell = column.onHeaderCell;
      const originalOnCell = column.onCell;
      // const isFirstColumn = columnIndex === 0;
      const isLastColumn = columnIndex === baseColumns.length - 1;
      const isSecondLastColumn = columnIndex === baseColumns.length - 2;
      const cellBorderClasses = cn(
        "!border-l-0",
        // isFirstColumn && "!border-l-0",
        "!border-r !border-b border-border_solid",
        isSecondLastColumn && "!border-r-0",
        isLastColumn && "!border-r-0 !border-l",
      );

      return {
        ...column,
        onHeaderCell: (col) => {
          const headerCell = originalOnHeaderCell?.(col);

          return {
            ...headerCell,
            className: cn(headerCell?.className, cellBorderClasses),
          };
        },
        onCell: (record: Transaction, rowIndex?: number) => {
          const cell = originalOnCell?.(record, rowIndex);

          return {
            ...cell,
            className: cn(cellBorderClasses, cell?.className),
          };
        },
      };
    });
  }, [isPc, currentType, currency, onEdit, onDelete]);

  return (
    <VirtualTableView<Transaction>
      loading={isLoading}
      rowKey="id"
      rowHeight={52}
      pagination={false}
      enableInfiniteScroll={enableInfiniteScroll}
      onLoadMore={onLoadMore}
      loadingMore={loadingMore}
      hasMore={hasMore}
      loadMoreThreshold={loadMoreThreshold}
      loadingMoreContent={loadingMoreContent}
      noMoreContent={noMoreContent}
      dataSource={data}
      columns={columns}
      scroll={{ x: "max-content" }}
      sticky={{ offsetHeader: 0 }}
      className={cn(
        styles.transactionTableScope,
        className,
        data.length === 0 && styles.nodata,
      )}
      rowClassName={() => "bg-transparent text-left"}
    />
  );
};
